import { useState } from 'preact/hooks';
import { useTranslation } from '../i18n';
import { MobileDrawer, PlatformPills } from '../primitives';
import { LocaleSwitcher } from './LocaleSwitcher';
import { NotificationBell } from './notifications/NotificationBell';
import { Navbar, NavbarAdminPill, NavbarUserChip } from './shared/Navbar';

/**
 * Platform top nav. Composes the unified platform `<Navbar>`:
 *
 *   [mark] Gposingway[ / Section]    [Articles] [Pack] [Studio] [Browse catalog]    [Admin?] [chip] [Locale]
 *
 * The current section's pill picks up the accent-fill marker; the brand line grows a breadcrumb
 * path matching it ("Gposingway / Emporium" on /emporium, "Gposingway / Articles" on /articles).
 * When <c>current</c> is <c>null</c> (the landing root, sign-in, legal pages) the breadcrumb
 * stays clean - just the wordmark - matching the legacy <c>LandingNav</c>.
 *
 * Admin pill only renders for signed-in admins (role-gated via /me); user chip renders the
 * three-state identity affordance (loading / anonymous "Sign in" / authenticated avatar +
 * sign-out). LocaleSwitcher lives at the trailing edge so readers can pick their language from
 * any page.
 *
 * Below md, PlatformPills and LocaleSwitcher hide from their usual slots and move into a
 * hamburger-triggered MobileDrawer instead - otherwise mobile readers have no way to reach
 * Articles/Pack/Studio/Browse or the five non-English locales.
 */
export function EmporiumNav({ current = 'emporium' as const }: { current?: 'emporium' | 'articles' | 'studio' | 'pack' | null } = {}) {
    const t = useTranslation();
    const brandPath = current ? brandPathFor(current, t) : undefined;
    const [navOpen, setNavOpen] = useState(false);
    return (
        <>
            <Navbar
                brand={brandPath ? { path: brandPath } : undefined}
                homeHref="/"
                homeAriaLabel="Gposingway home"
                middleSlot={<PlatformPills current={current ?? undefined} />}
                actions={
                    <>
                        <button
                            type="button"
                            onClick={() => setNavOpen(true)}
                            aria-label={t('nav.openMenu')}
                            data-testid="nav-hamburger"
                            class="inline-flex h-9 w-9 items-center justify-center rounded border border-rule text-fg-primary transition-colors hover:border-accent hover:text-accent md:hidden"
                        >
                            <span aria-hidden="true" class="text-lg leading-none">≡</span>
                        </button>
                        <NavbarAdminPill />
                        <NotificationBell />
                        <NavbarUserChip />
                        <LocaleSwitcher />
                    </>
                }
            />

            <MobileDrawer
                open={navOpen}
                onClose={() => setNavOpen(false)}
                title={t('nav.menu')}
                closeLabel={t('nav.closeMenu')}
                triggerBreakpoint="md"
            >
                <div class="flex flex-col gap-6 p-4">
                    <PlatformPills current={current ?? undefined} mobile />
                    <div class="border-t border-rule pt-4">
                        <LocaleSwitcher mobile />
                    </div>
                </div>
            </MobileDrawer>
        </>
    );
}

function brandPathFor(
    section: 'emporium' | 'articles' | 'studio' | 'pack',
    t: ReturnType<typeof useTranslation>,
): string[] {
    switch (section) {
        case 'emporium': return [t('emporium.navBrand')];
        case 'articles': return [t('landing.navArticles')];
        // Pack + Studio pills link off-platform; if for any reason a future surface mounts the
        // nav with current=pack/studio (e.g. an in-app pack-overview page), the breadcrumb echo
        // is still meaningful.
        case 'pack': return [t('landing.navPack')];
        case 'studio': return [t('landing.navStudio')];
    }
}
